import React, { useEffect, useMemo, useRef, useState } from 'react';
import Sheet, { FILL, FILL_ROW, PILL_BASE, pill } from './Sheet';
import { SearchIcon, PinOnlyIcon } from '../canvas/icons';
import searchService from '../../../services/searchService';
import portalService from '../../../services/portalService';
import { searchPlaces, searchCities, distanceKm } from '../../../services/placeSearch';
import { whenLabel } from '../../EventLinkPicker';

/**
 * The three linking sheets (handoff v3 §5.4 and §6): LINK AN EVENT, ADD A VENUE and WHERE ARE
 * YOU?. Each is a search pill and a list of pill rows with a tag on the right that turns cyan
 * once picked (LINKED / ADDED / HERE). Picking closes the sheet. REMOVE FROM PORTAL shows when
 * something is linked.
 *
 * Cities are always nearest first (share/city/manage handoff §A): the anchor is the device's
 * location, then the posting city's coordinates, then the profile's home town geocoded — never
 * the catalogue's stored order. The venue sheet starts with a CITY pill, the posting city and the
 * five nearest towns with their distance; CITY switches the same sheet into CHOOSE A CITY. A
 * query with no place in the chosen city searches wider and shows the nearest matches.
 */
const TAG_ON = { background: '#00FFFF', color: '#000' };
const TAG_OFF = { background: '#fff', color: '#000' };
const LOCATION_KEY = 'pr.userLocation';
const GEO_DENIED_KEY = 'pr.geoDenied';
export const town = (label) => (label || '').split(',')[0].trim();
const same = (a, b) => town(a).toLowerCase() === town(b).toLowerCase();

/** `HERE` under 1km, `7.1 KM` under ten, else a whole number. */
export function kmLabel(km) {
  if (!Number.isFinite(km)) return '';
  if (km < 1) return 'HERE';
  if (km < 10) return `${km.toFixed(1)} KM`;
  return `${Math.round(km)} KM`;
}

function storedLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    const loc = raw ? JSON.parse(raw) : null;
    return loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lng) ? { lat: loc.lat, lng: loc.lng } : null;
  } catch { return null; }
}

/** One ask of the device, remembered if refused. */
function deviceLocation() {
  return new Promise((resolve) => {
    try {
      if (localStorage.getItem(GEO_DENIED_KEY) === '1' || typeof navigator === 'undefined' || !navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => { try { localStorage.setItem(GEO_DENIED_KEY, '1'); } catch { /* fine */ } resolve(null); },
        { maximumAge: 600000, timeout: 6000 },
      );
    } catch { resolve(null); }
  });
}

/**
 * Where "nearest" is measured from: the device, the posting city, then the profile's home town
 * (geocoded once). Null only when none of those can be had.
 */
export function useCityAnchor(city, homeLabel = '') {
  const [device, setDevice] = useState(storedLocation);
  const [home, setHome] = useState(null);
  useEffect(() => {
    if (device) return undefined;
    let done = false;
    deviceLocation().then((loc) => { if (!done && loc) setDevice(loc); });
    return () => { done = true; };
  }, [device]);
  const cityAnchor = city?.lat != null && city?.lng != null ? { lat: city.lat, lng: city.lng } : null;
  useEffect(() => {
    if (device || cityAnchor || !homeLabel) return undefined;
    let done = false;
    searchCities(homeLabel).then((r) => { if (!done && r[0]?.lat != null) setHome({ lat: r[0].lat, lng: r[0].lng }); });
    return () => { done = true; };
  }, [device, cityAnchor, homeLabel]);
  return device || cityAnchor || home;
}

const withKm = (anchor) => (c) => ({ ...c, km: anchor && c.lat != null && c.lng != null ? distanceKm(anchor.lat, anchor.lng, c.lat, c.lng) : Infinity });
const byNear = (a, b) => a.km - b.km || (a.label || '').localeCompare(b.label || '');

/** The catalogue, nearest first, and whether the posting city is really a region. */
export function useNearbyCities(city, anchor) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    let done = false;
    portalService.listCities?.()?.then?.((r) => { if (!done && r?.success) setCatalog(r.data || []); }).catch?.(() => {});
    return () => { done = true; };
  }, []);
  return useMemo(() => {
    const cities = catalog.map(withKm(anchor)).sort(byNear);
    const label = city?.label || '';
    const isRegion = !!label && !cities.some((c) => same(c.label, label))
      && cities.some((c) => town(c.region || '').toLowerCase() === town(label).toLowerCase());
    return { cities, isRegion };
  }, [catalog, anchor, city?.label]);
}

export function SearchPill({ value, onChange, placeholder }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44, padding: '0 16px', background: FILL, borderRadius: 999 }}>
      <SearchIcon />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{ flex: 1, minWidth: 0, background: 'transparent', color: '#fff', border: 0, fontFamily: 'inherit', fontSize: 12, outline: 'none', boxShadow: 'none' }}
      />
    </label>
  );
}

export function Rows({ rows, empty, note = '' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="sheet-rows">
      {note && rows.length > 0 && <span data-testid="sheet-note" style={{ padding: '2px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.6)' }}>{note}</span>}
      {rows.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={r.pick}
          className="font-mono"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left', padding: '10px 10px 10px 20px', background: FILL_ROW, color: '#fff', border: 0, borderRadius: 999, fontFamily: 'inherit', cursor: 'pointer', transition: 'background-color .2s' }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</span>
            {r.meta && <span style={{ fontSize: 9, letterSpacing: '0.08em', color: 'rgba(255,255,255,.65)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.meta}</span>}
          </span>
          <span style={{ flexShrink: 0, height: 28, display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: 999, ...(r.on ? TAG_ON : TAG_OFF), fontSize: 10, fontWeight: 700, letterSpacing: '0.12em' }}>{r.tag}</span>
        </button>
      ))}
      {rows.length === 0 && empty && (
        <span style={{ padding: '10px 4px', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.6)' }}>{empty}</span>
      )}
    </div>
  );
}

function RemoveButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} className="prc-pill" style={{ ...PILL_BASE, height: 40, background: FILL, color: '#fff', fontSize: 11, letterSpacing: '0.14em' }}>
      REMOVE FROM PORTAL
    </button>
  );
}

function useDebounced(fn, ms, deps) {
  useEffect(() => {
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** LINK AN EVENT: the public catalogue, newest keystroke wins. */
export function EventSheet({ value, onPick, onRemove, onClose }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const reqId = useRef(0);
  useDebounced(async () => {
    const id = ++reqId.current;
    setSearching(true);
    const r = await searchService.searchEvents({ q: q.trim(), take: 8 });
    if (id !== reqId.current) return;
    setSearching(false);
    setRows(r?.success ? (r.data?.items || []) : []);
  }, 150, [q]);

  const list = rows.map((item) => ({
    key: item.id,
    title: item.title,
    meta: whenLabel(item.startsAtUtc) || item.meta,
    tag: value?.id === item.id ? 'LINKED' : 'LINK',
    on: value?.id === item.id,
    pick: () => onPick(item),
  }));
  return (
    <Sheet title="LINK AN EVENT" suffix="· OPTIONAL" onClose={onClose} testId="event-sheet">
      <SearchPill value={q} onChange={setQ} placeholder="Search events by name, venue or city…" />
      <Rows rows={list} empty={searching ? 'SEARCHING…' : 'NO EVENTS MATCH.'} />
      {value && <RemoveButton onClick={onRemove} />}
    </Sheet>
  );
}

/** The 48×28 switch the WINDOW sheet uses, as a full-width row. */
export function VenueOpenRow({ on, onChange, mode = 'open' }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label="Show the place right away" data-testid="venue-open-row" onClick={() => onChange(!on)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 10px 10px 16px', background: FILL_ROW, color: '#fff', border: 0, borderRadius: 24, fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em' }}>SHOW THE PLACE RIGHT AWAY</span>
        <span style={{ fontSize: 10, lineHeight: 1.45, color: 'rgba(255,255,255,.65)' }}>
          {on ? 'Everyone who can see the Portal sees the place and address.' : mode === 'curated' ? 'Off: the place stays hidden until you let them in.' : 'Off: the place stays hidden until they tap I’M DOWN.'}
        </span>
      </span>
      <span aria-hidden style={{ position: 'relative', flexShrink: 0, width: 48, height: 28, borderRadius: 999, background: on ? '#00FFFF' : 'rgba(255,255,255,.2)', transition: 'background-color .2s' }}>
        <span style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
      </span>
    </button>
  );
}

/**
 * The city list both CHOOSE A CITY and WHERE ARE YOU? show: the eight nearest with no query, every
 * match nearest first with one (the catalogue plus a Mapbox search), rows "Penticton ·
 * OKANAGAN, BC · 11 KM", HERE on the posting city.
 */
export function useCityRows({ q, city, cities, anchor, onPick }) {
  const [found, setFound] = useState([]);
  const reqId = useRef(0);
  useDebounced(async () => {
    const id = ++reqId.current;
    if (!q.trim()) { setFound([]); return; }
    const items = await searchCities(q);
    if (id !== reqId.current) return;
    setFound(items);
  }, 200, [q]);
  return useMemo(() => {
    const query = q.trim().toLowerCase();
    const seen = new Set();
    const pool = query
      ? [...cities.filter((c) => `${c.label} ${c.region || ''}`.toLowerCase().includes(query)), ...found.map(withKm(anchor))].sort(byNear)
      : cities.slice(0, 8);
    return pool
      .filter((c) => { const k = town(c.label).toLowerCase(); if (!k || seen.has(k)) return false; seen.add(k); return true; })
      .map((c) => {
        const on = same(c.label, city?.label);
        const where = (c.region || c.label.split(',').slice(1).join(',').trim()).toUpperCase();
        return { key: c.label, title: town(c.label), meta: [where, kmLabel(c.km)].filter(Boolean).join(' · '), tag: on ? 'HERE' : 'SET', on, pick: () => onPick(c) };
      });
  }, [q, cities, found, anchor, city?.label, onPick]);
}

/** ADD A VENUE: places near the posting city, the venue lock once one is linked, CHOOSE A CITY inside. */
export function VenueSheet({ value, city, onCity, onPick, onRemove, onClose, venueOpen = false, onVenueOpen = null, mode = 'open', homeLabel = '' }) {
  const [q, setQ] = useState('');
  const [cityMode, setCityMode] = useState(false);
  const [rows, setRows] = useState([]);
  const [wider, setWider] = useState([]);
  const [searching, setSearching] = useState(false);
  const reqId = useRef(0);
  const anchor = useCityAnchor(city, homeLabel);
  const { cities, isRegion } = useNearbyCities(city, anchor);

  // A region is never a posting city: it becomes the nearest real town.
  useEffect(() => {
    if (isRegion && cities.length && cities[0].km !== Infinity) onCity(cities[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegion, cities.length]);

  useDebounced(async () => {
    const id = ++reqId.current;
    if (cityMode || !q.trim()) { setRows([]); setWider([]); setSearching(false); return; }
    setSearching(true);
    const cityCoords = city?.lat != null ? { lat: city.lat, lng: city.lng } : anchor;
    const local = (await searchPlaces(q, cityCoords)).filter((v) => !city?.label || !v.town || same(v.town, city.label));
    if (id !== reqId.current) return;
    if (local.length) { setRows(local); setWider([]); setSearching(false); return; }
    // Nothing in the chosen city: look wider, nearest first.
    const far = (await searchPlaces(q, anchor || cityCoords, { unbounded: true })).map((v) => ({ ...v, km: anchor && v.lat != null ? distanceKm(anchor.lat, anchor.lng, v.lat, v.lng) : Infinity })).sort(byNear);
    if (id !== reqId.current) return;
    setRows([]);
    setWider(far);
    setSearching(false);
  }, 200, [q, cityMode, city?.lat, city?.lng, city?.label]);

  const cityName = town(city?.label);
  const cityRows = useCityRows({ q: cityMode ? q : '', city, cities, anchor, onPick: (c) => { onCity(c); setCityMode(false); setQ(''); } });
  const pickRow = (v, elsewhere) => ({
    key: `${v.name}-${v.address}`,
    title: v.name,
    meta: elsewhere ? `${(v.town || '').toUpperCase()} · ${kmLabel(v.km)} AWAY` : (v.address || '').toUpperCase(),
    tag: value?.name === v.name && value?.address === v.address ? 'ADDED' : 'ADD',
    on: value?.name === v.name && value?.address === v.address,
    pick: () => {
      // A place in another town takes the posting city with it.
      if (elsewhere && v.town) onCity({ slug: null, label: v.town, lat: v.lat, lng: v.lng });
      onPick(v);
    },
  });
  const placeRows = rows.length ? rows.map((v) => pickRow(v, false)) : wider.map((v) => pickRow(v, true));
  const nearby = [
    ...(city?.label ? [{ ...city, km: 0, current: true }] : []),
    ...cities.filter((c) => !same(c.label, city?.label)).slice(0, 5),
  ];

  if (cityMode) {
    return (
      <Sheet title="CHOOSE A CITY" onClose={() => { setCityMode(false); setQ(''); }} testId="venue-sheet">
        <SearchPill value={q} onChange={setQ} placeholder="Search any city…" />
        <Rows rows={cityRows} note={q.trim() ? '' : 'NEAREST TO YOU'} empty="NO CITIES MATCH." />
      </Sheet>
    );
  }
  return (
    <Sheet title="ADD A VENUE" suffix="· OPTIONAL" onClose={onClose} testId="venue-sheet">
      {value && onVenueOpen && <VenueOpenRow on={venueOpen} onChange={onVenueOpen} mode={mode} />}
      <div className="prc-row" style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto' }} data-testid="city-pills">
        <button type="button" aria-label="Choose any city" title="Choose any city" onClick={() => { setCityMode(true); setQ(''); }} className="prc-pill" style={{ ...PILL_BASE, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', background: FILL, color: '#fff', fontSize: 10, letterSpacing: '0.12em' }}>
          <PinOnlyIcon size={12} />CITY
        </button>
        {nearby.map((c) => (
          <button key={c.label} type="button" aria-pressed={!!c.current} onClick={() => { if (!c.current) onCity(c); setQ(''); }} style={{ ...PILL_BASE, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 14px', ...pill(!!c.current), fontSize: 10, letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
            {town(c.label).toUpperCase()}
            {!c.current && <span style={{ fontSize: 9, letterSpacing: '0.06em', opacity: 0.6, fontWeight: 400 }}>{kmLabel(c.km)}</span>}
          </button>
        ))}
      </div>
      <SearchPill value={q} onChange={setQ} placeholder={cityName ? `Search a place in ${cityName}…` : 'Search a place…'} />
      <Rows
        rows={placeRows}
        note={wider.length && !rows.length ? `NOT IN ${cityName.toUpperCase()} · NEAREST MATCHES` : ''}
        empty={q.trim() ? (searching ? 'SEARCHING…' : `NO MATCHES NEAR ${cityName.toUpperCase() || 'YOU'}.`) : null}
      />
      {value && <RemoveButton onClick={onRemove} />}
    </Sheet>
  );
}

/** WHERE ARE YOU?: the posting city, the same list as CHOOSE A CITY. */
export function CitySheet({ value, onPick, onClose, homeLabel = '' }) {
  const [q, setQ] = useState('');
  const anchor = useCityAnchor(value, homeLabel);
  const { cities } = useNearbyCities(value, anchor);
  const rows = useCityRows({ q, city: value, cities, anchor, onPick });
  return (
    <Sheet title="WHERE ARE YOU?" onClose={onClose} testId="city-sheet">
      <SearchPill value={q} onChange={setQ} placeholder="Search any city…" />
      <Rows rows={rows} note={q.trim() ? '' : 'NEAREST TO YOU'} empty="NO CITIES MATCH." />
    </Sheet>
  );
}
